import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Monitor, Share } from 'lucide-react';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dontRemindAgain, setDontRemindAgain] = useState(false);

  useEffect(() => {
    // Check if running as standalone app
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has permanently dismissed the prompt
      const permanentlyDismissed = localStorage.getItem('pwa-prompt-never-show');
      if (permanentlyDismissed) return;
      
      // Show prompt if not dismissed recently  
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const lastDismissed = dismissed ? new Date(dismissed) : null;
      const daysSinceDismissed = lastDismissed ? 
        (new Date() - lastDismissed) / (1000 * 60 * 60 * 24) : Infinity;
      
      // Show more frequently for testing: after 1 day instead of 7
      if (daysSinceDismissed > 1) {
        setTimeout(() => setShowPrompt(true), 2000); // Show after 2 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Auto-show for iOS after some time if not installed
    if (iOS && !standalone) {
      const timer = setTimeout(() => {
        // Check if user has permanently dismissed the prompt
        const permanentlyDismissed = localStorage.getItem('pwa-prompt-never-show');
        if (permanentlyDismissed) return;
        
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        const lastDismissed = dismissed ? new Date(dismissed) : null;
        const daysSinceDismissed = lastDismissed ? 
          (new Date() - lastDismissed) / (1000 * 60 * 60 * 24) : Infinity;
        
        if (daysSinceDismissed > 0.1) { // Show iOS prompt after 2.4 hours instead of 3 days
          setShowPrompt(true);
        }
      }, 10000); // Show after 10 seconds instead of 30

      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    if (dontRemindAgain) {
      localStorage.setItem('pwa-prompt-never-show', 'true');
    } else {
      localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    }
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto">
      <div className="bg-white rounded-lg shadow-lg border border-stone-200 p-4 animate-slide-up">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-azure-500 rounded-lg flex items-center justify-center mr-3 overflow-hidden">
              <img 
                src="/icons/icon-96x96.png" 
                alt="Paper Sedge icon" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-sm">
                Install Paper Sedge
              </h3>
              <p className="text-xs text-slate-600">
                Add to your home screen for quick access
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-stone-400 hover:text-slate-600 p-1"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center space-x-2 text-xs text-slate-600 mb-3">
          <Smartphone size={14} />
          <span>Works offline</span>
          <span>•</span>
          <Monitor size={14} />
          <span>App-like experience</span>
        </div>

        {/* Don't Remind Me Again Checkbox */}
        <div className="flex items-center space-x-2 mb-3">
          <input
            type="checkbox"
            id="dont-remind-again"
            checked={dontRemindAgain}
            onChange={(e) => setDontRemindAgain(e.target.checked)}
            className="w-4 h-4 text-azure-600 bg-stone-100 border-stone-300 rounded focus:ring-azure-500 focus:ring-2"
          />
          <label htmlFor="dont-remind-again" className="text-xs text-slate-600 cursor-pointer">
            Don't remind me again
          </label>
        </div>

        {isIOS ? (
          <div className="space-y-2">
            <button
              onClick={handleDismiss}
              className="w-full bg-azure-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-azure-600 transition-colors"
            >
              Got it!
            </button>
            <div className="text-xs text-slate-600 space-y-1">
              <p>To install:</p>
              <p>1. Tap the share button <Share size={14} className="inline-block mx-1" /></p>
              <p>2. Select "Add to Home Screen"</p>
            </div>
          </div>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleDismiss}
              className="flex-1 text-slate-600 py-2 px-3 rounded-lg text-sm font-medium hover:bg-stone-100 transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 bg-azure-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-azure-600 transition-colors flex items-center justify-center space-x-1"
            >
              <Download size={14} />
              <span>Install</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;